import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/joy';
import { useNavigate } from 'react-router-dom';
import Card from 'components/core/Card';
import Stack from 'components/core/Stack';
import Typography from 'components/core/Typography';
import Grid from 'components/core/Grid';
import Sheet from 'components/core/Sheet';
import IconButton from 'components/core/IconButton';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';

import InfoLine from './InfoLine';
import DossierActionButtons from './DossierActionButtons';
import DossierPrintButton from './DossierPrintButton';
import CommonUtils from 'utils/CommonUtils';

import Event from 'domain/Event';

const EventDossier = ({ event, notes, firstObservation, lastObservation }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const dossierRef = useRef();

    if (!event) return null;

    const dossierSchema = [
        {
            id: 'type',
            label: 'Event Type',
            value: event.get('type')
        },
        {
            id: 'startDate',
            label: 'Start Date',
            value: event.get('startDate'),
            dateSpecificity: event.get('dateSpecificity')
        },
        {
            id: 'endDate',
            label: 'End Date',
            value: event.get('endDate'),
            dateSpecificity: event.get('dateSpecificity')
        },
        {
            id: 'description',
            label: 'Description',
            value: event.get('description')
        }
    ];

    return (
        <Sheet 
            ref={dossierRef}
            sx={{
                width: '100%',
                background: 'none',
                mt: 2,
                mb: 2
            }}
        >
            <Card>
                <Grid container>
                    <Grid item xs={12}>
                        <Stack direction='row' spacing={6} alignItems='end' justifyContent='space-between'>
                            <Stack direction='row' spacing={2} alignItems='end'>
                                <Typography variant='h3'>
                                    EVENT {event.get('name')}
                                </Typography>
                                <Typography variant='h4' style={{marginBottom: 3}}>
                                    {event.getByPath(['type', 'label'])}
                                </Typography>
                            </Stack>
                            <Stack direction='row' spacing={1} className="dossier-action-buttons">
                                <DossierPrintButton 
                                    dossierRef={dossierRef} 
                                    title={`Event - ${event.getByPath(['type', 'label'])}`} 
                                />
                                <DossierActionButtons className="Event" id={event.id} />
                            </Stack>
                        </Stack>
                    </Grid>
                    
                    {dossierSchema.map(({label, value, id, dateSpecificity}) => (
                        <InfoLine 
                            key={id}
                            id={id}
                            label={label}
                            value={value}
                            dateSpecificity={dateSpecificity}
                        />
                    ))}

                    {firstObservation && (
                        <InfoLine
                            id='firstObservationDate'
                            label='Date of First Observation'
                            value={firstObservation.get('date')}
                            dateSpecificity={firstObservation.get('dateSpecificity')}
                        />
                    )}
                    
                    {lastObservation && (
                        <InfoLine
                            id='lastObservationDate'
                            label='Date of Most Recent Observation'
                            value={lastObservation.get('date')}
                            dateSpecificity={lastObservation.get('dateSpecificity')}
                        />
                    )}

                    {event.get('locations') && event.get('locations').length > 0 && (
                        <Grid item xs={12}>
                            <Stack sx={{ mt: 2 }}>
                                <Typography variant='body2'>
                                    Associated Locations
                                </Typography>
                                <Card sx={{background: theme.palette.primary[50]}}>
                                    <Grid container>
                                        {event.get('locations').map((location, index) => (
                                            <Grid item xs={12} key={index}>
                                                <Stack direction="row" alignItems="center">
                                                    <Typography variant='body1' sx={{ mr: 1 }}>
                                                        {location.get('label')}
                                                    </Typography>
                                                    <IconButton size="sm" onClick={() => navigate(`/location/${location.id}`)}>
                                                        <ArrowOutwardIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Card>
                            </Stack>
                        </Grid>
                    )}

                    {event.get('note') && (
                        <Grid item xs={12}>
                            <Stack sx={{ mt: 2 }}>
                                <Typography variant='body2'>
                                    Event Note
                                </Typography>
                                <Card sx={{background: theme.palette.secondary[50]}}>
                                    <Typography variant='body1'>
                                        {event.get('note')}
                                    </Typography>
                                </Card>
                            </Stack>
                        </Grid>
                    )}
                </Grid>
            </Card>
        </Sheet>
    );
}; 

EventDossier.propTypes = {
    event: PropTypes.instanceOf(Event).isRequired,
    notes: PropTypes.array,
    firstObservation: PropTypes.object,
    lastObservation: PropTypes.object
};

EventDossier.defaultProps = {
    notes: [],
    firstObservation: null,
    lastObservation: null
};

export default EventDossier;